import { FC } from 'react'
import '../../styles/responsive.css'
import { CardNameLiteral } from '../../types/apiResponse/tankInfoSettingTypes'
import { DistCard } from './cards/DistributionCard'
import { GaugeCard } from './cards/GaugeCard'
import { GraphCard } from './cards/GraphCard'
import { MemoCard } from './cards/MemoCard'
import { SpecCard } from './cards/SpecCard'
import { TankVolumeCard } from './cards/TankVolumeCard'

export type TankInfoCardContainerProps = {
  tankId: string
  title: 'Water' | 'Fish' | 'Tank'
  cardNames: CardNameLiteral[]
}

// 카드별 설정값 (API 응답 데이터에 맞춤)
const gaugeSettings: Record<string, { minWarning: number; maxWarning: number; minLimit: number; maxLimit: number; metric: string }> = {
  nh3: { minWarning: 0, maxWarning: 0.025, minLimit: 0, maxLimit: 0.1, metric: 'mg/L' },
  no2: { minWarning: 0, maxWarning: 0.5, minLimit: 0, maxLimit: 2, metric: 'mg/L' },
  do: { minWarning: 40, maxWarning: 250, minLimit: 0, maxLimit: 300, metric: 'mg/L' },
  ph: { minWarning: 6.2, maxWarning: 8, minLimit: 0, maxLimit: 14, metric: '' },
  waterLevel: { minWarning: 0.5, maxWarning: 4, minLimit: 0, maxLimit: 5, metric: 'm' },
  temperature: { minWarning: 14, maxWarning: 18, minLimit: 0, maxLimit: 40, metric: '°C' },
}

const specSettings: Record<string, { specName: string; metric: string; subSpec: Array<{ specName: string; metric: string }> }> = {
  count: {
    specName: 'count',
    metric: '',
    subSpec: [
      { specName: 'STOCK', metric: '' },
      { specName: 'MORTALITY', metric: '' },
      { specName: 'HARVEST', metric: '' },
    ],
  },
  stockingDensity: {
    specName: 'stockingDensity',
    metric: 'kg/m³',
    subSpec: [],
  },
}

const graphSettings: Record<string, { metric: string }> = {
  sampleWeight: { metric: 'g' },
  averageWeight: { metric: 'g' },
  biomass: { metric: 'kg' },
  kFactor: { metric: '' },
}

const defaultGaugeSetting = { minWarning: 0, maxWarning: 100, minLimit: 0, maxLimit: 100, metric: '' }
const defaultGraphSetting = { metric: '' }

export const TankInfoCardContainer: FC<TankInfoCardContainerProps> = ({
  tankId,
  title,
  cardNames,
}) => {

  return (
    <>
      {cardNames.length !== 0 ? (
        <div className="my-6 md:my-[40px]">
          <div className="font-bold text-lg md:text-[1.25rem] text-tdplindigo mb-2 md:mb-[7.5px]">
            {title}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-[15px]">
            {cardNames.map((cardName, index) => {
              // memo 카드
              if (cardName === 'memo') {
                return <MemoCard tankId={tankId} key={index} />
              }

              // tankVolume 카드
              if (cardName === 'tankVolume') {
                return <TankVolumeCard tankId={tankId} key={index} />
              }

              // 카드 타입 결정
              const cardType = getDefaultCardType(cardName)

              // distribution 카드
              if (cardType === 'distribution') {
                return <DistCard key={index} tankId={tankId} cardName={cardName} />
              }

              // gauge 카드
              if (cardType === 'gauge') {
                const setting = gaugeSettings[cardName] || defaultGaugeSetting
                return (
                  <GaugeCard
                    key={index}
                    tankId={tankId}
                    cardName={cardName}
                    gaugeSetting={setting}
                  />
                )
              }

              // spec 카드
              if (cardType === 'spec') {
                const setting = specSettings[cardName]
                if (!setting) return null
                return (
                  <SpecCard
                    key={index}
                    tankId={tankId}
                    cardName={cardName}
                    specSetting={setting}
                  />
                )
              }

              // graph 카드
              if (cardType === 'graph') {
                const setting = graphSettings[cardName] || defaultGraphSetting
                return (
                  <GraphCard
                    key={index}
                    tankId={tankId}
                    cardName={cardName}
                    graphSetting={setting}
                  />
                )
              }

              return null
            })}
          </div>
        </div>
      ) : (
        <></>
      )}
    </>
  )
}

// 카드 이름에서 기본 카드 타입 추출 (API 응답 데이터 기준)
function getDefaultCardType(cardName: CardNameLiteral): 'gauge' | 'spec' | 'graph' | 'distribution' {
  // Water cards: gauge 타입 (nh3, no2, do, ph, temperature)
  const gaugeCards = ['nh3', 'no2', 'do', 'ph', 'waterLevel', 'temperature']
  // Fish cards: spec 타입 (count with sub-specs)
  const specCards = ['count']
  // Fish cards: graph 타입 (array data - sampleWeight, averageWeight, biomass, kFactor, stockingDensity)
  const graphCards = ['sampleWeight', 'averageWeight', 'biomass', 'kFactor', 'stockingDensity']

  if (gaugeCards.includes(cardName)) {
    return 'gauge'
  }
  if (specCards.includes(cardName)) {
    return 'spec'
  }
  if (graphCards.includes(cardName)) {
    return 'graph'
  }

  return 'graph'
}

export default TankInfoCardContainer
